import { useState, useCallback } from 'react';
import { useAccount } from 'wagmi';
import { useRecoilValue } from 'recoil';
import {
  userSmartAccount,
  userSmartAccountUsdtBalance,
  userSmartAccountRainBalance,
} from '../../RecoilAtom';

/**
 * Thin wrapper that reads the universal wallet + smart account state
 * from Recoil (set by WalletConnect) and exposes a consistent API
 * for the GenerateMarkets feature.
 *
 * No longer creates its own smart account — reuses the app-wide one.
 */
export const useWallet = ({ connectWalletModal } = {}) => {
  const [walletError, setWalletError] = useState(null);

  const { address, isConnected } = useAccount();
  const smartAccountAddress = useRecoilValue(userSmartAccount);
  const balanceUSDT = useRecoilValue(userSmartAccountUsdtBalance);
  const balanceRAIN = useRecoilValue(userSmartAccountRainBalance);

  const wallet = {
    connected: isConnected && !!address && !!smartAccountAddress,
    address: address || null,
    balanceUSDT: +balanceUSDT || 0,
    balanceRAIN: +balanceRAIN || 0,
    smartAccountAddress: smartAccountAddress || null,
  };

  const connectWallet = useCallback(async () => {
    try {
      if (connectWalletModal) {
        connectWalletModal();
      } else {
        console.warn('No wallet modal configured. Pass connectWalletModal prop.');
      }
    } catch {
      setWalletError('not-connected');
    }
  }, [connectWalletModal]);

  const validateLiquidity = useCallback((amount, currency) => {
    if (amount < 0.01) return 'min-liquidity';
    const balance = currency === 'usdr' ? wallet.balanceUSDT : wallet.balanceRAIN;
    if (amount > balance) return 'insufficient-balance';
    return null;
  }, [wallet.balanceUSDT, wallet.balanceRAIN]);

  const getBalance = useCallback((currency) => {
    return currency === 'usdr' ? wallet.balanceUSDT : wallet.balanceRAIN;
  }, [wallet.balanceUSDT, wallet.balanceRAIN]);

  return {
    wallet,
    walletError,
    setWalletError,
    connectWallet,
    validateLiquidity,
    getBalance,
  };
};
